import { useMemo } from 'react';
import type { FieldContent } from '@capture/shared';
import { generateSampleBatch, isFormatlessAdapter } from '@capture/shared';
import { useExportFormats } from '../../hooks/useExportFormats';
import { useFormConfigStore, resolveV3, activeForm } from '../state/store';
import { MappingPanel } from './MappingPanel';
import styles from './export-mapping-tab.module.css';

interface ExportMappingTabProps {
  jobTypeId: string;
  /** The form whose per-destination mappings are being edited. */
  activeFormId: string | null;
}

/**
 * Plan 3 Task 6 — the Form-Config "export mapping" tab. Job-level destinations
 * are managed on Job Config (Task 5); here, for the ACTIVE form, we render one
 * mapping panel per destination, editing
 * `forms[activeFormId].exportMappings[destinationId].mappings`.
 *
 * Id-scoping (Task-4/9 lesson): the draft store is a singleton whose
 * `activeFormId` can drift from this tab's `activeFormId` prop. We resolve the
 * form ONCE and use its `id` for BOTH the read selector and the write action so
 * the two can never target different forms.
 */
export function ExportMappingTab({ jobTypeId, activeFormId }: ExportMappingTabProps) {
  const contentV3 = useFormConfigStore((s) => s.contentV3);
  const content = useFormConfigStore((s) => s.content);
  const updateFormDestinationMapping = useFormConfigStore((s) => s.updateFormDestinationMapping);
  const getFormDestinationMappings = useFormConfigStore((s) => s.getFormDestinationMappings);
  const { formats } = useExportFormats();

  const v3 = resolveV3({ contentV3, content });

  // Resolve the active form once — id used for every read AND write below.
  const form = useMemo(() => (v3 ? activeForm(v3, activeFormId) : null), [v3, activeFormId]);

  const fieldNames = useMemo(() => (form?.fields ?? []).map((f) => f.internalName ?? f.name), [form]);

  // Sample data drives MappingPanel's (collapsed-by-default) live preview.
  const sampleData = useMemo(
    () => generateSampleBatch((form?.fields ?? []) as FieldContent[], 3),
    [form],
  );

  if (!v3 || !form) return null;

  const destinations = v3.exportDestinations;

  if (destinations.length === 0) {
    return (
      <div className={styles.tab}>
        <div className={styles.empty}>
          <p>
            No export destinations yet. Add an export destination on{' '}
            <strong>Job Config → Export Destinations</strong> first, then map this
            form's fields to it here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tab}>
      <p className={styles.intro}>
        Map <strong>{form.name}</strong>'s fields to each export destination's
        columns. Destinations themselves are configured on Job Config.
      </p>

      {destinations.map((dest) => {
        // F-033 — only adapters that genuinely render their own artifact
        // (dossier / document-repository) skip mapping. A destination with no
        // BOUND format is not the same thing: sql's inline-structure path
        // REQUIRES column mappings (its INSERT columns come from them), so it
        // renders the MappingPanel driven by the destination's own
        // structure/columns instead of the format's.
        const formatless = isFormatlessAdapter(dest.adapter);
        const fmt = formatless
          ? undefined
          : formats.find((f) => f.id === dest.exportFormatId);

        return (
          <section key={dest.id} className={styles.section}>
            <div className={styles.sectionHeader}>
              <h3 className={styles.sectionName}>{dest.name}</h3>
              <span className={styles.sectionAdapter}>{dest.adapter}</span>
            </div>
            <div className={styles.sectionBody}>
              {formatless ? (
                <p className={styles.noMapping} data-testid={`no-mapping-${dest.id}`}>
                  No field mapping required — this destination renders its own
                  artifact.
                </p>
              ) : (
                <MappingPanel
                  mappings={getFormDestinationMappings(form.id, dest.id)}
                  fieldNames={fieldNames}
                  format={dest.format}
                  structure={dest.structure}
                  formatColumns={fmt ? fmt.columns : []}
                  formatStructure={fmt?.structure ?? undefined}
                  boundFormatName={fmt?.name}
                  sampleData={sampleData}
                  onChange={(m) => updateFormDestinationMapping(form.id, dest.id, m)}
                  jobTypeId={jobTypeId}
                  destinationId={dest.id}
                />
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
